import { Participant } from 'livekit-client';
import { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { MobileSpeaker } from 'features/ParticipantItem/presenters/Mobile';
import { ParticipantItem } from 'features/ParticipantItem';
import { ControlsPanel } from 'features/Room/components';
import { useVideoQualityByParticipantCount } from 'hooks/useVideoQualityByParticipantCount';
import { useParticipantSubscriptions } from 'hooks/useParticipantSubscriptions';
import { isElectron } from '@web-chat-core/ui-kit/helpers/electron';
import { STAGE_LAYOUT } from '../../../consts';
import { useSpeakerTrackQuality } from '../hooks/useSpeakerTrackQuality';
import { StageProps } from '../types';
import { useSort } from '../hooks/useSort';
import { ControlsArea, HeaderLayout, MainLayout, SidebarLayout, SpeakerMobileLayout } from './view';
import { MobileParticipantsList } from './components/MobileParticipantsList/MobileParticipantsList';
import { Toolbar } from './components/Toolbar';

type SpeakerStageProps = StageProps & { participants: Participant[] };

export const MobileSpeakerStage = observer(
    ({ roomState, controlRenderer, onLeave, participants }: SpeakerStageProps) => {
        const { t } = useTranslation();
        const { room } = roomState;
        const [participantInFocus, ...otherParticipants] = participants;

        const isSidebarHidden = otherParticipants.length === 0;

        const enableScreenShare = isElectron ? true : false;

        useVideoQualityByParticipantCount({
            isSidebarMode: false,
            stageLayout: STAGE_LAYOUT.SPEAKER,
        });
        const focusParticipantList = useMemo(
            () => (participantInFocus ? [participantInFocus] : []),
            [participantInFocus],
        );

        // Для спикера в главном окне подписка не требует IntersectionObserver
        // но используем единый хук для консистентности
        useParticipantSubscriptions({
            room,
            participants: focusParticipantList,
            maxVisible: 1,
            isSidebarMode: true,
            speakerParticipantId: participantInFocus?.identity,
        });
        useSpeakerTrackQuality(participantInFocus);
        const { getSortedParticipantsWhenFocused } = useSort();
        const sortedParticipants = getSortedParticipantsWhenFocused();

        // TODO: Demo modernization. Невозможная ситуация, подумать как лучше убрать.
        if (!room) {
            return <div>{t('web_call_room_closed')}</div>;
        }

        const ControlRenderer = controlRenderer ?? ControlsPanel;

        return (
            <SpeakerMobileLayout $isWide={isSidebarHidden}>
                <HeaderLayout>
                    <Toolbar participants={sortedParticipants} />
                </HeaderLayout>

                <MainLayout>
                    <ParticipantItem
                        key={participantInFocus.identity}
                        width="100%"
                        height="100%"
                        participant={participantInFocus}
                        PresenterComponent={MobileSpeaker}
                        isHideSpeakingBorder
                    />
                </MainLayout>

                <SidebarLayout $hidden={isSidebarHidden}>
                    <MobileParticipantsList
                        participants={otherParticipants}
                        room={room}
                        speakerParticipantId={participantInFocus?.identity}
                        isHideSpeakingBorder
                    />
                </SidebarLayout>

                <ControlsArea>
                    <ControlRenderer room={room} enableScreenShare={enableScreenShare} onLeave={onLeave} />
                </ControlsArea>
            </SpeakerMobileLayout>
        );
    },
);
